import React, { useEffect, useRef, useState } from 'react';
import { Mic, Camera, Volume2, Wifi, Check, X, Loader2 } from 'lucide-react';

const STATE_ICON = {
  idle: <Loader2 className="h-4 w-4 text-gray-400" />,
  testing: <Loader2 className="h-4 w-4 animate-spin text-red-700" />,
  ok: <Check className="h-4 w-4 text-emerald-600" />,
  fail: <X className="h-4 w-4 text-red-600" />,
};

const Row = ({ icon, label, state, note, testid }) => (
  <div className="flex items-center gap-3 py-2" data-testid={testid}>
    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">{icon}</div>
    <div className="flex-1 min-w-0">
      <div className="text-xs font-bold text-gray-900">{label}</div>
      {note && <div className="text-[10px] text-gray-500 truncate">{note}</div>}
    </div>
    <div className="flex-shrink-0" data-testid={`${testid}-state`}>{STATE_ICON[state] || STATE_ICON.idle}</div>
  </div>
);

const DeviceCheckPanel = ({ onDone }) => {
  const [cam, setCam] = useState({ state: 'idle', note: '' });
  const [mic, setMic] = useState({ state: 'idle', note: '' });
  const [spk, setSpk] = useState({ state: 'idle', note: '' });
  const [net, setNet] = useState({ state: 'idle', note: '' });
  const videoRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioLoopRef = useRef(null);
  const [level, setLevel] = useState(0);

  useEffect(() => () => {
    if (audioLoopRef.current) cancelAnimationFrame(audioLoopRef.current);
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (_e) {} }
  }, []);

  const testCamera = async () => {
    setCam({ state: 'testing', note: 'Requesting camera…' });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) videoRef.current.srcObject = stream;
      const label = stream.getVideoTracks()[0]?.label || 'Default camera';
      setCam({ state: 'ok', note: label });
    } catch (e) {
      setCam({ state: 'fail', note: (e.name === 'NotAllowedError')
        ? 'Permission denied — click the padlock icon in your browser to enable camera access.'
        : 'Could not access camera. Ensure no other app is using it.' });
    }
  };

  const testMic = async () => {
    setMic({ state: 'testing', note: 'Requesting microphone…' });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const loop = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setLevel(Math.min(1, avg / 96));
        audioLoopRef.current = requestAnimationFrame(loop);
      };
      loop();
      const label = stream.getAudioTracks()[0]?.label || 'Default microphone';
      setMic({ state: 'ok', note: `${label} — speak to see the level bar` });
    } catch (e) {
      setMic({ state: 'fail', note: (e.name === 'NotAllowedError')
        ? 'Permission denied — enable microphone in your browser settings.'
        : 'Could not access microphone.' });
    }
  };

  const testSpeakers = () => {
    setSpk({ state: 'testing', note: 'Playing test tone…' });
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = 440;
      g.gain.value = 0.06;
      o.connect(g); g.connect(ctx.destination);
      o.start();
      setTimeout(() => {
        o.stop(); ctx.close();
        setSpk({ state: 'ok', note: 'Test tone played. If you heard it, speakers are working.' });
      }, 600);
    } catch (e) {
      setSpk({ state: 'fail', note: 'Could not play audio.' });
    }
  };

  const testNetwork = async () => {
    setNet({ state: 'testing', note: 'Pinging Winhouse servers…' });
    try {
      const t0 = performance.now();
      // small no-op fetch — bust cache
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/houses/leaderboard?_=${Date.now()}`,
                    { cache: 'no-store' });
      const dt = Math.round(performance.now() - t0);
      let quality = 'Excellent';
      if (dt > 400) quality = 'Poor';
      else if (dt > 200) quality = 'Fair';
      else if (dt > 100) quality = 'Good';
      setNet({ state: dt > 800 ? 'fail' : 'ok', note: `${dt} ms · ${quality}` });
    } catch (_e) {
      setNet({ state: 'fail', note: 'Offline — check your connection.' });
    }
  };

  const runAll = () => { testCamera(); testMic(); testSpeakers(); testNetwork(); };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5" data-testid="device-check-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-red-700">Pre-class check</div>
          <div className="text-sm font-bold text-black">Camera, mic, speakers, network</div>
        </div>
        <button onClick={runAll}
          className="h-9 px-3 rounded-full bg-black hover:bg-gray-800 text-white text-[10px] uppercase tracking-wider font-black"
          data-testid="device-check-run-all">Run all</button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-xl bg-gray-50 p-3">
          <Row icon={<Camera className="h-4 w-4 text-gray-700" />} label="Camera" state={cam.state} note={cam.note} testid="device-check-camera" />
          <button onClick={testCamera}
            className="h-8 w-full mt-1 rounded-lg bg-white border border-gray-200 hover:bg-gray-100 text-[10px] uppercase tracking-wider font-bold"
            data-testid="device-check-camera-btn">Test camera</button>
          <video ref={videoRef} autoPlay muted playsInline
            className="w-full mt-2 rounded-lg bg-black aspect-video object-cover"
            data-testid="device-check-camera-preview" />
        </div>

        <div className="rounded-xl bg-gray-50 p-3">
          <Row icon={<Mic className="h-4 w-4 text-gray-700" />} label="Microphone" state={mic.state} note={mic.note} testid="device-check-mic" />
          <button onClick={testMic}
            className="h-8 w-full mt-1 rounded-lg bg-white border border-gray-200 hover:bg-gray-100 text-[10px] uppercase tracking-wider font-bold"
            data-testid="device-check-mic-btn">Test microphone</button>
          <div className="mt-2 h-2 bg-black/10 rounded overflow-hidden">
            <div className="h-full bg-red-600 transition-all"
              style={{ width: `${Math.round(level * 100)}%` }}
              data-testid="device-check-mic-level" />
          </div>
        </div>

        <div className="rounded-xl bg-gray-50 p-3">
          <Row icon={<Volume2 className="h-4 w-4 text-gray-700" />} label="Speakers" state={spk.state} note={spk.note} testid="device-check-spk" />
          <button onClick={testSpeakers}
            className="h-8 w-full mt-1 rounded-lg bg-white border border-gray-200 hover:bg-gray-100 text-[10px] uppercase tracking-wider font-bold"
            data-testid="device-check-spk-btn">Play test tone</button>
        </div>

        <div className="rounded-xl bg-gray-50 p-3">
          <Row icon={<Wifi className="h-4 w-4 text-gray-700" />} label="Internet latency" state={net.state} note={net.note} testid="device-check-net" />
          <button onClick={testNetwork}
            className="h-8 w-full mt-1 rounded-lg bg-white border border-gray-200 hover:bg-gray-100 text-[10px] uppercase tracking-wider font-bold"
            data-testid="device-check-net-btn">Test network</button>
        </div>
      </div>

      {onDone && (
        <div className="mt-3 flex justify-end">
          <button onClick={onDone}
            className="h-9 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider"
            data-testid="device-check-done">Looks good — continue</button>
        </div>
      )}
    </div>
  );
};

export default DeviceCheckPanel;
